import { Delivery, Sales_order, Sales_order_l, roundHalfAway } from '@app/shared/_models/sales';
import { OrderLineType, SALES_ORDER_MODE_ASAP_DELIVERY, sales_order_mode } from '@app/shared/constants';
import { C_value } from '@app/shared/_models/common';
import { parseTz } from '@app/shared/time';
import { lineClosed, orderLineNamed, rentalSubtitle, rentalTitle } from '@app/sales/rental-billing/rental-billing.labels';

// Données d'ouverture de la fenêtre de livraison location : lignes à mettre sur le BL, ou un BL précis (même signé, en lecture).
// `collectIds` : matériels posés à reprendre (Récupérer) : leur ligne louée va sur le BL à −1, aucune ligne de reprise (JP 29/10).
// `saleOrderIds` : commandes de vente du dossier à livrer dans ce passage (lignes cochées d'office), sur leur propre BL (JP 30/10).
export interface RentalDeliveryData { id: number; lineIds?: number[]; collectIds?: number[]; saleOrderIds?: number[]; deliveryId?: number; patient?: string; }   // patient : camion du Planning, seuls ses matériels sont proposés

// Une ligne du dossier vue de la fenêtre : installation, reprise ou article vendu.
export interface RentalDeliveryRow {
  id: number;
  orderId: number;      // dossier, ou commande de vente liée : chaque commande a son BL dans le passage
  seq: number;
  typ: string;
  product: number;
  name: string;         // l'article, qui nomme le matériel dans les messages
  title: string;        // désignation affichée : intitulé du contrat de location, l'article sans contrat
  article: string | null; // l'article sous l'intitulé du contrat
  packaging: string;
  patient: string;
  qty: number;          // sur ce BL, ou ce qu'il reste à livrer pour une ligne à ajouter ; négative = reprise du matériel
  elsewhere: number[];  // autres BL non signés qui la portent (garde serveur : un par sens, une pose et sa reprise depuis le 04/11)
  closed: boolean;      // clôture 2107 en vigueur : aucune pose (le serveur la refuse), la reprise −1 reste permise
}

// Clôture en vigueur d'une ligne de la pièce : la dernière de rental_lifecycle.closures, non annulée (même règle que closure.rs).
export function orderLineClosed(l: Sales_order_l): boolean {
  const closures = (l.rental_lifecycle as any)?.closures;
  return lineClosed({ closure: Array.isArray(closures) && closures.length ? closures[closures.length - 1] : null });
}

// Une pose (+) sur une ligne close est refusée par le serveur ; sa reprise (−1) ne l'est pas.
export function closedPose(r: RentalDeliveryRow): boolean {
  return r.closed && r.qty > 0;
}

// realdatdel posée = BL signé, figé (même règle que DeliveryComponent.isDeliveryDelivered).
export function isSigned(d: Delivery | null | undefined): boolean {
  const raw: any = d?.realdatdel;
  if (raw === null || raw === undefined) return false;
  if (typeof raw === 'number') return raw !== 0;
  const s = String(raw).trim();
  return s !== '' && s !== '0';
}

// BL où l'on peut encore écrire : non signé, hors bon d'annulation.
export function openDeliveries(order: Sales_order): Delivery[] {
  return (order.lnk_deliveries ?? []).filter((d) => !isSigned(d) && (Number(d.mode) || 0) >= 0);
}

const qtyOn = (d: Delivery, lineId: number): number =>
  (d.lnk_deliveryl ?? []).filter((l) => Number(l.id_order_l) === lineId).reduce((s, l) => s + (Number(l.qty) || 0), 0);

// BL affiché : celui demandé ; un BL à créer (null) dès qu'un matériel demandé, ou à défaut proposé (`wanted`), n'est sur aucun BL ouvert, car une planification de plus
// ne défait jamais une autre (JP 31/10) ; sinon le BL ouvert qui porte une ligne demandée, sinon le dernier BL ouvert.
export function pickActive(order: Sales_order, lineIds: number[], deliveryId?: number, collect: readonly number[] = [], wanted: (r: RentalDeliveryRow) => boolean = () => true): Delivery | null {
  const all = order.lnk_deliveries ?? [];
  if (deliveryId) { const d = all.find((x) => x.id === deliveryId); if (d) return d; }
  const free = rowsOf(order, null, collect).toPlan.filter((r) => !closedPose(r));
  if (lineIds.length ? free.some((r) => lineIds.includes(r.id)) : free.some(wanted)) return null;
  const open = openDeliveries(order);
  return open.find((d) => lineIds.some((id) => qtyOn(d, id) !== 0)) ?? open[open.length - 1] ?? null;
}

// Lecture « pending » : tous les BL d'une ligne lue sont lus avec elle, le reste se calcule donc ici comme au serveur (insert_remaining_delivery_lines).
// Reste à livrer = qté − posé (BL positifs) ; à reprendre (−) : la ligne hors contrat pas encore reprise, et le matériel posé ou à poser demandé par `collect`.
// Pose et reprise pointent la ligne louée du contrat : une ancienne ligne RENTAL_RETURN n'est jamais proposée ; sur ce BL, elle est montrée à part (`legacy`) et n'en fait plus partie.
export function rowsOf(order: Sales_order, active: Delivery | null, collect: readonly number[] = []): { onBl: RentalDeliveryRow[]; toPlan: RentalDeliveryRow[]; elsewhere: RentalDeliveryRow[]; legacy: RentalDeliveryRow[] } {
  const all = order.lnk_deliveries ?? [];
  const open = openDeliveries(order);
  const onBl: RentalDeliveryRow[] = [], toPlan: RentalDeliveryRow[] = [], elsewhere: RentalDeliveryRow[] = [], legacy: RentalDeliveryRow[] = [];
  for (const l of (order.lines ?? []) as Sales_order_l[]) {
    if (!l.product_name) continue;
    const id = Number(l.id);
    const old = l.typ === OrderLineType.RENTAL_RETURN;
    const rental = l.typ === OrderLineType.RENTAL;
    const lineQty = Number(l.qty) || 0;
    const posed = all.reduce((s, d) => s + Math.max(qtyOn(d, id), 0), 0);
    const net = all.reduce((s, d) => s + qtyOn(d, id), 0);
    const onSite = all.filter(isSigned).reduce((s, d) => s + qtyOn(d, id), 0);
    const here = active ? qtyOn(active, id) : 0;
    const otherBls = open.filter((d) => d !== active && qtyOn(d, id) !== 0);
    const others = otherBls.map((d) => d.id);
    const named = orderLineNamed(l);
    const row = (qty: number): RentalDeliveryRow => ({
      id, orderId: Number(order.id) || 0, seq: Number(l.seq) || 0, typ: String(l.typ ?? ''), product: Number(l.product) || 0, name: l.product_name ?? '',
      title: rentalTitle(named) ?? '', article: rentalSubtitle(named),
      packaging: l.packaging_name ?? '', patient: (l.memo ?? '').trim(), qty, elsewhere: others, closed: orderLineClosed(l),
    });
    const remaining = roundHalfAway(lineQty - posed, 4);
    // Récupérer : le posé signé plus la pose planifiée sur un autre BL ouvert (JP 04/11), jamais à côté d'une reprise déjà ouverte.
    const openPose = otherBls.reduce((s, d) => s + Math.max(qtyOn(d, id), 0), 0);
    const collectable = rental && collect.includes(id) && remaining <= 0 && otherBls.every((d) => qtyOn(d, id) > 0) && onSite + openPose > 0;
    if (old) { if (here !== 0) legacy.push(row(here)); continue; }
    if (here !== 0) onBl.push(row(here));
    else if (collectable) toPlan.push(row(-(onSite + openPose)));
    else if (others.length) elsewhere.push(row(qtyOn(otherBls[0], id)));
    else if (remaining > 0) toPlan.push(row(remaining));
    else if (rental && lineQty < 0 && net > lineQty) toPlan.push(row(roundHalfAway(lineQty - net, 4)));
  }
  return { onBl, toPlan, elsewhere, legacy };
}

// Jour et créneau prévus d'un BL, pour son onglet et les confirmations : « 31/10/2026 · Matin » ; un BL emporté ou en retrait dit son mode.
export function blWhen(d: Pick<Delivery, 'mode' | 'askdatdel' | 'askslot'> | null | undefined, slots: readonly C_value[], format = 'DD/MM/YYYY'): string {
  if (!d) return '';
  const mode = Number(d.mode) || 0;
  if (mode > 0 && mode < SALES_ORDER_MODE_ASAP_DELIVERY) return sales_order_mode.get(mode) ?? '';
  const day = parseTz(d.askdatdel)?.format(format) ?? 'sans date';
  const slot = Number(d.askslot) || 0;
  const name = slot ? slots.find((s) => Number(s.id) === slot)?.name ?? '' : '';
  return name ? `${day} · ${name}` : day;
}

// Lignes envoyées au serveur pour le BL : celles cochées, quantité entière de la ligne (pas de livraison partielle d'un matériel) ; −1 = reprise.
export function deliveryLinesOf(chosen: ReadonlyMap<number, number>, deliveryId: number): { id_delivery: number; id_order_l: number; qty: number }[] {
  return [...chosen.entries()].filter(([, qty]) => qty !== 0).map(([id_order_l, qty]) => ({ id_delivery: deliveryId, id_order_l, qty }));
}

// L'UPDATE d'une F2 en création efface et réinsère ses lignes (sync_order_lines) : ancien id → id neuf, appariés par seq, que l'INSERT garde ; un seq en double n'est pas apparié.
export function renumberedLines(before: readonly { id: number; seq: number }[], after: readonly { id: number; seq: number }[]): Map<number, number> {
  const unique = (ls: readonly { id: number; seq: number }[]) => {
    const n = new Map<number, number>();
    for (const l of ls) n.set(Number(l.seq), (n.get(Number(l.seq)) ?? 0) + 1);
    return new Map(ls.filter((l) => n.get(Number(l.seq)) === 1).map((l) => [Number(l.seq), Number(l.id)]));
  };
  const neu = unique(after);
  return new Map([...unique(before)].flatMap(([seq, id]): [number, number][] => (neu.has(seq) && id ? [[id, neu.get(seq)!]] : [])));
}

// Commandes de vente où un BL neuf se pose : en création (validée par le passage qui la livre, #27), validée, en cours de livraison ; jamais scellée ni annulée.
const PLANNABLE_SALE_STATES: ReadonlySet<string> = new Set(['CREATED', 'PREPARATION', 'DELIVERED']);
const DEAD_SALE_STATES: ReadonlySet<string> = new Set(['CANCELED', 'REFUSED']);

export function deliverableSaleOrders(orders: Sales_order[] | null | undefined): Sales_order[] {
  return (orders ?? []).filter((o) => o.doc_type === 'FACTURE' && PLANNABLE_SALE_STATES.has(String(o.state)));
}

// BL d'une commande de vente dans le passage : son BL ouvert rattaché au BL du dossier (id_planning, écrit à sa création), jamais deviné ; sinon un BL à créer.
export function saleActive(order: Sales_order, passageId: number): Delivery | null {
  return passageId > 0 ? openDeliveries(order).find((d) => Number(d.id_planning) === passageId) ?? null : null;
}

// Commande de vente vue du passage : `plannable` = ses restes se proposent ; `dead` = annulée, son BL ouvert est retiré à l'enregistrement.
export interface PassageSale { order: Sales_order; active: Delivery | null; plannable: boolean; dead: boolean; }

// Audit 31/10 #30 : toute F2 dont un BL ouvert désigne le passage est montrée, quel que soit son état (facturée, il se livre encore ; annulée, il se retire),
// car le serveur refuse de signer le BL du dossier tant qu'un tel BL reste ouvert ; s'y ajoutent les F2 qui se livrent encore.
export function passageSales(orders: Sales_order[] | null | undefined, passageId: number): PassageSale[] {
  return (orders ?? []).filter((o) => o.doc_type === 'FACTURE').flatMap((order) => {
    const active = saleActive(order, passageId);
    const plannable = PLANNABLE_SALE_STATES.has(String(order.state));
    return plannable || active ? [{ order, active, plannable, dead: DEAD_SALE_STATES.has(String(order.state)) }] : [];
  });
}

// En-tête du BL d'une commande de vente : la planification du passage (jour, créneau, mode, consigne, durée), ses seules lignes cochées, rattaché au BL du dossier.
export function salePassageDelivery(passage: any, orderId: number, active: Delivery | null, passageId: number): any {
  return { ...passage, id: Number(active?.id) || 0, id_order: orderId, id_planning: Number(active?.id_planning) || passageId || 0, realdatdel: null, lnk_attachs: [], lnk_deliveryl: [] };
}
